import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

export default function RotatingSphere() {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf0f0f0); // 保持原來的淺灰色背景

    const camera = new THREE.PerspectiveCamera(
      75,
      containerRef.current.clientWidth / containerRef.current.clientHeight,
      0.1,
      1000
    );
    camera.position.z = 5;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(
      containerRef.current.clientWidth,
      containerRef.current.clientHeight
    );
    containerRef.current.appendChild(renderer.domElement);

    const sphereGroup = new THREE.Group();
    scene.add(sphereGroup);

    // 创建透明球体参考 - 放大包住所有黑白带子
    const sphereGeometry = new THREE.SphereGeometry(2.5, 32, 32);
    const sphereMaterial = new THREE.MeshBasicMaterial({
      color: 0xcccccc,
      wireframe: true,
      transparent: true,
      opacity: 0.1,
    });
    const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
    sphereGroup.add(sphere);

    // 在球体中心添加四个立方体
    const cubeSize = 0.27;
    const cubeGeometry = new THREE.BoxGeometry(cubeSize, cubeSize, cubeSize);

    const cubeEdgesMaterial = new THREE.LineBasicMaterial({
      color: 0x000000,
      linewidth: 1,
    });

    const cubeEdgesMaterialBlue = new THREE.LineBasicMaterial({
      color: 0x0066ff,
      linewidth: 1,
      transparent: true,
      opacity: 0.3,
    });

    const cubeEdgesMaterialPurple = new THREE.LineBasicMaterial({
      color: 0x9933ff,
      linewidth: 1,
      transparent: true,
      opacity: 0.3,
    });

    const cubeEdges1 = new THREE.EdgesGeometry(cubeGeometry);
    const cube1 = new THREE.LineSegments(cubeEdges1, cubeEdgesMaterial);
    sphereGroup.add(cube1);

    const cubeEdges2 = new THREE.EdgesGeometry(cubeGeometry);
    const cube2 = new THREE.LineSegments(cubeEdges2, cubeEdgesMaterialBlue);
    cube2.rotation.y = Math.PI / 4;
    cube2.rotation.x = Math.PI / 6;
    sphereGroup.add(cube2);

    const cubeEdges3 = new THREE.EdgesGeometry(cubeGeometry);
    const cube3 = new THREE.LineSegments(cubeEdges3, cubeEdgesMaterialPurple);
    cube3.rotation.y = Math.PI / 3;
    cube3.rotation.z = Math.PI / 5;
    sphereGroup.add(cube3);

    const cubeEdges4 = new THREE.EdgesGeometry(cubeGeometry);
    const cube4 = new THREE.LineSegments(cubeEdges4, cubeEdgesMaterial);
    cube4.rotation.x = Math.PI / 3;
    cube4.rotation.y = -Math.PI / 6;
    cube4.rotation.z = Math.PI / 4;
    sphereGroup.add(cube4);

    // 创建文字带 - 背景透明版本
    const createTextBand = (
      text,
      radius,
      color,
      fontSize,
      rotationX,
      rotationY,
      rotationZ
    ) => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = 4096;
      canvas.height = 512;

      // 清空画布，保持透明背景
      context.clearRect(0, 0, canvas.width, canvas.height);

      // 绘制文字
      context.fillStyle = color;
      context.font = `bold ${fontSize}px Arial`;
      context.textAlign = 'left';
      context.textBaseline = 'middle';

      const textWithSpace = text + '        ';
      const textWidth = context.measureText(textWithSpace).width;
      const repeatCount = Math.ceil(canvas.width / textWidth) + 2;

      for (let i = 0; i < repeatCount; i++) {
        context.fillText(textWithSpace, i * textWidth, canvas.height / 2);
      }

      const texture = new THREE.CanvasTexture(canvas);
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.ClampToEdgeWrapping;

      // 创建圆柱形的文字带
      const segments = 128;
      const geometry = new THREE.BufferGeometry();
      const vertices = [];
      const uvs = [];
      const indices = [];

      const bandHeight = 0.6;

      for (let i = 0; i <= segments; i++) {
        const angle = (i / segments) * Math.PI * 2;
        const x = Math.cos(angle) * radius;
        const z = Math.sin(angle) * radius;

        // 上边缘
        vertices.push(x, bandHeight / 2, z);
        uvs.push((i / segments) * 3, 0);

        // 下边缘
        vertices.push(x, -bandHeight / 2, z);
        uvs.push((i / segments) * 3, 1);

        if (i < segments) {
          const base = i * 2;
          indices.push(base, base + 1, base + 2);
          indices.push(base + 1, base + 3, base + 2);
        }
      }

      geometry.setAttribute(
        'position',
        new THREE.Float32BufferAttribute(vertices, 3)
      );
      geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
      geometry.setIndex(indices);
      geometry.computeVertexNormals();

      const material = new THREE.MeshBasicMaterial({
        map: texture,
        side: THREE.DoubleSide,
        transparent: true,
        alphaTest: 0.1, // 添加alphaTest，只有alpha > 0.1的像素才会渲染
        depthWrite: false, // 关闭深度写入，让透明部分不遮挡后面的物体
      });

      const mesh = new THREE.Mesh(geometry, material);

      // 应用旋转，让每条带子朝向不同方向
      mesh.rotation.x = rotationX;
      mesh.rotation.y = rotationY;
      mesh.rotation.z = rotationZ;

      return mesh;
    };

    // 添加两条文字带在最外圈，它们会在±15度范围内做相对运动
    const textBand1 = createTextBand(
      'TAG ART GUIDE',
      2.7,
      '#000000',
      80,
      0,
      0,
      0
    );
    sphereGroup.add(textBand1);

    const textBand2 = createTextBand(
      'tagartguide.com',
      2.65,
      '#000000',
      60,
      0,
      0,
      0
    );
    sphereGroup.add(textBand2);

    // 创建白色线条（圆环）- 在球体内侧，紧贴球体
    const whiteRingGeometry = new THREE.TorusGeometry(2.48, 0.02, 16, 100);
    const whiteRingMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const whiteRing = new THREE.Mesh(whiteRingGeometry, whiteRingMaterial);
    whiteRing.rotation.x = Math.PI / 4;
    sphereGroup.add(whiteRing);

    // 创建黑色线条（圆环）- 在球体外侧，紧贴球体
    const blackRingGeometry = new THREE.TorusGeometry(2.52, 0.03, 16, 100);
    const blackRingMaterial = new THREE.MeshBasicMaterial({ color: 0x000000 });
    const blackRing = new THREE.Mesh(blackRingGeometry, blackRingMaterial);
    blackRing.rotation.y = Math.PI / 3;
    blackRing.rotation.x = Math.PI / 6;
    sphereGroup.add(blackRing);

    // 创建断裂带子 - 只有正面有颜色，背面透明
    const createBrokenBand = (
      radius,
      thickness,
      color,
      rotationX,
      rotationY,
      rotationZ
    ) => {
      const segments = 200;
      const visibleRanges = [
        { start: 0.05, end: 0.2 },
        { start: 0.5, end: 0.65 },
      ];

      const group = new THREE.Group();

      visibleRanges.forEach(range => {
        const geometry = new THREE.BufferGeometry();
        const vertices = [];
        const indices = [];

        const startSegment = Math.floor(range.start * segments);
        const endSegment = Math.ceil(range.end * segments);

        for (let i = startSegment; i <= endSegment; i++) {
          const progress = i / segments;
          const angle = progress * Math.PI * 2;
          const x = Math.cos(angle) * radius;
          const z = Math.sin(angle) * radius;

          const localIndex = (i - startSegment) * 2;

          vertices.push(x, thickness / 2, z);
          vertices.push(x, -thickness / 2, z);

          if (i > startSegment) {
            const base = localIndex - 2;
            indices.push(base, base + 1, base + 2);
            indices.push(base + 1, base + 3, base + 2);
          }
        }

        geometry.setAttribute(
          'position',
          new THREE.Float32BufferAttribute(vertices, 3)
        );
        geometry.setIndex(indices);
        geometry.computeVertexNormals();

        // 只渲染正面，背面不渲染（透明效果）
        const material = new THREE.MeshBasicMaterial({
          color: color,
          side: THREE.FrontSide, // 只显示正面
          transparent: false,
        });

        const mesh = new THREE.Mesh(geometry, material);
        group.add(mesh);
      });

      group.rotation.x = rotationX;
      group.rotation.y = rotationY;
      group.rotation.z = rotationZ;

      return group;
    };

    // 粗的黑白断裂带子在内圈
    const thickBlackBand = createBrokenBand(
      2.3,
      0.36,
      0x000000,
      Math.PI / 5,
      Math.PI / 4,
      0
    );
    sphereGroup.add(thickBlackBand);

    const thickWhiteBand = createBrokenBand(
      2.4,
      0.36,
      0xffffff,
      -Math.PI / 6,
      Math.PI / 5,
      Math.PI / 7
    );
    sphereGroup.add(thickWhiteBand);

    let isDragging = false;
    let previousMouse = { x: 0, y: 0 };
    let targetRotation = { x: 0.0008, y: 0.0015 };
    let time = 0; // 用于计算夹角波动

    const handleMouseMove = event => {
      const rect = containerRef.current.getBoundingClientRect();
      const currentX = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const currentY = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      if (isDragging) {
        const deltaX = currentX - previousMouse.x;
        const deltaY = currentY - previousMouse.y;

        sphereGroup.rotation.y += deltaX * 2;
        sphereGroup.rotation.x += deltaY * 2;
      }

      previousMouse.x = currentX;
      previousMouse.y = currentY;
    };

    const handleMouseDown = () => {
      isDragging = true;
      if (containerRef.current) {
        containerRef.current.style.cursor = 'grabbing';
      }
    };

    const handleMouseUp = () => {
      isDragging = false;
      if (containerRef.current) {
        containerRef.current.style.cursor = 'grab';
      }
    };

    containerRef.current.addEventListener('mousemove', handleMouseMove);
    containerRef.current.addEventListener('mousedown', handleMouseDown);
    containerRef.current.addEventListener('mouseup', handleMouseUp);
    containerRef.current.addEventListener('mouseleave', handleMouseUp);

    const animate = () => {
      requestAnimationFrame(animate);

      // 只有在不拖动时，球体像地球一样绕Y轴（南北极轴）自西向东旋转（慢10倍）
      if (!isDragging) {
        sphereGroup.rotation.y += 0.0005; // 自西向东旋转，速度慢10倍
      }

      // 粗带子在内圈自己旋转
      thickBlackBand.rotation.y += 0.004;
      thickWhiteBand.rotation.y -= 0.0035;

      // 文字带做受限的相对运动，夹角在±15度范围内波动
      time += 0.01;
      const maxAngle = Math.PI / 12; // 15度 = π/12 弧度

      // 第一条文字带做缓慢的正弦波动
      textBand1.rotation.z = Math.sin(time * 0.3) * maxAngle * 0.5;
      textBand1.rotation.x = Math.cos(time * 0.25) * maxAngle * 0.3;

      // 第二条文字带做不同频率的波动，保持夹角在±15度内
      textBand2.rotation.z = Math.sin(time * 0.35) * maxAngle * 0.5;
      textBand2.rotation.x = Math.cos(time * 0.28) * maxAngle * 0.3;

      renderer.render(scene, camera);
    };

    animate();

    const handleResize = () => {
      if (!containerRef.current) return;
      camera.aspect =
        containerRef.current.clientWidth / containerRef.current.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(
        containerRef.current.clientWidth,
        containerRef.current.clientHeight
      );
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (containerRef.current && renderer.domElement) {
        containerRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100vh',
        cursor: 'grab',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 700 700'%3E%3Cdefs%3E%3Cfilter id='nnnoise-filter' x='-20%25' y='-20%25' width='140%25' height='140%25' filterUnits='objectBoundingBox' primitiveUnits='userSpaceOnUse' color-interpolation-filters='linearRGB'%3E%3CfeTurbulence type='turbulence' baseFrequency='0.167' numOctaves='4' seed='15' stitchTiles='stitch' x='0%25' y='0%25' width='100%25' height='100%25' result='turbulence'%3E%3C/feTurbulence%3E%3CfeSpecularLighting surfaceScale='28' specularConstant='0.75' specularExponent='20' lighting-color='%23000' x='0%25' y='0%25' width='100%25' height='100%25' in='turbulence' result='specularLighting'%3E%3CfeDistantLight azimuth='3' elevation='100'%3E%3C/feDistantLight%3E%3C/feSpecularLighting%3E%3C/filter%3E%3C/defs%3E%3Crect width='700' height='700' fill='%23ffffff'%3E%3C/rect%3E%3Crect width='700' height='700' fill='%23000000' filter='url(%23nnnoise-filter)' opacity='0.15'%3E%3C/rect%3E%3C/svg%3E")`,
        backgroundSize: '500px 500px',
        backgroundRepeat: 'repeat',
      }}
    />
  );
}
